import { useEffect, useState, useCallback, useMemo } from 'react';
import { throttle } from 'lodash-es';
import { css } from '@emotion/react';
import { tokens, Button, Loader, IconButton } from '@hotjar/hotsauce';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { Icon } from '@hotjar/webapp/components/Icon';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Site } from '@hotjar/site';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useCurrentUser } from '@hotjar/user';

import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import type { BlockedIp } from '../types';
import { ipBlockingApi } from '../api';
import { Section, Separator, StyledInput as Input } from '../styles';

const InputsCss = css`
  width: 196px;
  text-transform: none;
  font-weight: normal;
`;

const InputWithErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0;
`;

interface IpBlockingProps {
  site: Site;
  onFormDirty: (isDirty: boolean) => void;
  onFormSaved: () => void;
}

type IpBlockingHook = {
  data?: BlockedIp[];
  isLoading: boolean;
  error?: Error;
};

export function useIpBlocking(organizationId: number): IpBlockingHook {
  const [data, setData] = useState<BlockedIp[]>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState();

  useEffect(() => {
    async function load() {
      try {
        const response = await ipBlockingApi.get(organizationId);

        setData(response.data);
      } catch (e) {
        // @ts-expect-error fix catch error type
        setError(e);
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, [organizationId]);

  return {
    data,
    isLoading,
    error,
  };
}

const ButtonsContainer = styled.div`
  margin-top: ${tokens.spacer.x3};
`;

export const IpBlocking = ({ site, onFormDirty, onFormSaved }: IpBlockingProps): JSX.Element => {
  const { success, error: showError } = useNotify();

  const { t } = useTranslation();
  const { data: currentUser } = useCurrentUser();
  const { data, isLoading, error } = useIpBlocking(site.organization.id as number);

  const { control, register, errors, handleSubmit, reset, formState, getValues, setError } =
    useForm({
      mode: 'onBlur',
    });

  const { isDirty, isSubmitting } = formState;
  const [canSubmit, setCanSubmit] = useState(true);
  // The saving action is too fast,
  // So we throttle 'canSubmit' to let the user see the saving spinner.
  // FIXME: react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setCanSubmitThrottle = useCallback(
    throttle((nextValue: boolean) => {
      setCanSubmit(nextValue);
    }, 400),
    [],
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'blockedIps',
    keyName: '_id',
  });

  useEffect(() => {
    reset({ blockedIps: data });
    setCanSubmitThrottle(true);
    // FIXME: react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reset]);

  useEffect(() => {
    onFormDirty(isDirty);
  }, [isDirty, onFormDirty]);

  const siteOrgId = site?.organization.id ? `${site?.organization.id}` : null;
  const { hasPermission } = useAuthorization();
  const hasIpBlockingManagePermission = hasPermission(
    'organization',
    siteOrgId,
    'org-ignored-ips-manage',
  );

  const addIp = useCallback(() => {
    append({
      name: '',
      regex: '',
    });
  }, [append]);

  const validateRegex = useCallback(
    (regex: string) => {
      try {
        // Check that the regex is valid.
        RegExp(regex);
        // Check that there are no \d, e.g. \7, since that's a back-reference
        // and PostgreSQL will fail when it tries to match this.
        if (/\\\d/.test(regex)) {
          throw new Error('Backreference in IP regex');
        }
      } catch (e) {
        return t('common:site-settings.ip-blocking.is-required-regex');
      }
      return true;
    },
    [t],
  );

  const validateDuplicate = useCallback(
    (regex: string) => {
      if (!regex) {
        return true;
      }
      const ips = getValues().blockedIps as BlockedIp[];
      if (ips?.filter((ip) => ip.regex === regex)?.length > 1) {
        return t('common:site-settings.ip-blocking.is-duplicate');
      }
      return true;
    },
    [getValues, t],
  );

  const currentIpRegex = useMemo(
    () => currentUser?.currentIp?.replace(/\./g, '\\.'),
    [currentUser],
  );

  const currentName = currentUser?.name;

  const validateRequiredAndRemoveEmpty = useCallback(
    (blockedIps: BlockedIp[]) => {
      let isValid = true;
      const emptyFieldIndexes: number[] = [];
      blockedIps.forEach((blockedIp, index) => {
        // If regex is new and empty ignore validation.
        if (!blockedIp.id && !blockedIp.name && !blockedIp.regex) {
          emptyFieldIndexes.push(index);

          return;
        }

        if (!blockedIp.id) {
          if (!blockedIp.name && blockedIp.regex) {
            setError(`blockedIps[${index}].name`, {
              type: 'required',
              message: t('common:site-settings.ip-blocking.is-required-name') as string,
            });
            isValid = false;
          }
          if (!blockedIp.regex && blockedIp.name) {
            setError(`blockedIps[${index}].regex`, {
              type: 'required',
              message: t('common:site-settings.ip-blocking.is-required-regex') as string,
            });
            isValid = false;
          }
        }
      });

      // If everything else is valid remove new and empty fields.
      if (isValid) {
        remove(emptyFieldIndexes);
      }

      return isValid;
    },
    [remove, setError, t],
  );

  const selfBlockIp = useCallback(() => {
    append({
      name: `${currentName}'s IP`,

      regex: currentIpRegex,
    });
  }, [currentIpRegex, append, currentName]);

  if (error) {
    return <div>Error has occurred. Please try again.</div>;
  }

  if (isLoading) {
    return (
      <Section>
        <Loader
          isLoading
          css={css`
            margin: 6px auto;
          `}
        />
      </Section>
    );
  }

  const isCurrentIpAdded = fields?.find((ip) => ip.regex === currentIpRegex);

  const onSubmit = async ({ blockedIps }: { blockedIps: BlockedIp[] }) => {
    setCanSubmitThrottle(false);
    let isValid = false;
    let postData;

    if (typeof blockedIps === 'undefined') {
      isValid = true;
      postData = null;
    } else {
      isValid = validateRequiredAndRemoveEmpty(blockedIps);
      postData = blockedIps.filter((ip) => ip.name && ip.regex);
    }

    if (!isValid) {
      setCanSubmitThrottle(true);
      return;
    }
    const response = await ipBlockingApi.post(site.organization.id as number, postData);

    setCanSubmitThrottle(true);
    if (response.success) {
      success(t('common:site-settings.ip-blocking.save-success'));
      reset({ blockedIps: response?.data?.ignored_ips });
      onFormSaved();
    } else {
      showError(t('common:site-settings.ip-blocking.save-error'));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Section>
        {!fields.length && (
          <div
            css={css`
              color: ${tokens.color.text_default};
            `}
          >
            <Icon
              icon="empty-tray"
              css={css`
                margin-right: ${tokens.spacer.x1};
              `}
              color={tokens.color.neutral_700}
              size={16}
            />
            {t('common:site-settings.ip-blocking.no-ips-blocked')}
          </div>
        )}

        {fields.map((blockedIp, blockedIpIndex) => {
          const errorObj = errors.blockedIps ? errors.blockedIps[blockedIpIndex] : {};
          const isSelfIp = blockedIp.regex === currentIpRegex;
          register({ name: `blockedIps[${blockedIpIndex}].id`, value: blockedIp.id });
          const nameRegister = blockedIp.id
            ? {
                required: t('common:site-settings.ip-blocking.is-required-name') as string,
              }
            : {};
          const regexRegisterOptions = blockedIp.id
            ? {
                required: t('common:site-settings.ip-blocking.is-required-regex') as string,
                validate: { validateRegex, validateDuplicate },
              }
            : {
                validate: { validateRegex, validateDuplicate },
              };
          return (
            <div
              css={css`
                display: flex;
                margin-bottom: 8px;
              `}
              // FIXME: no-underscore-dangle
              // eslint-disable-next-line no-underscore-dangle
              key={blockedIp._id}
            >
              <InputWithErrorContainer>
                <Tooltip
                  enabled={isSelfIp}
                  content={t('common:site-settings.ip-blocking.your-ip') as string}
                  placement="top"
                >
                  <span>
                    <Input
                      defaultValue={blockedIp.name}
                      name={`blockedIps[${blockedIpIndex}].name`}
                      placeholder={t('common:site-settings.ip-blocking.ip-label-placeholder')}
                      inputRef={register(nameRegister)}
                      isError={!!errors.name}
                      disabled={!hasIpBlockingManagePermission}
                      containerCss={InputsCss}
                      css={css`
                        border-top-right-radius: 0;
                        border-bottom-right-radius: 0;
                      `}
                    />
                  </span>
                </Tooltip>
                <span
                  css={css`
                    color: ${tokens.color.text_danger};
                  `}
                >
                  {errorObj?.name?.message}
                </span>
              </InputWithErrorContainer>

              <InputWithErrorContainer>
                <Input
                  name={`blockedIps[${blockedIpIndex}].regex`}
                  defaultValue={blockedIp.regex}
                  placeholder={t('common:site-settings.ip-blocking.ip-value-placeholder')}
                  inputRef={register(regexRegisterOptions)}
                  isError={!!errors.name}
                  disabled={!hasIpBlockingManagePermission}
                  containerCss={[
                    InputsCss,
                    css`
                      width: 289px;
                    `,
                  ]}
                  css={css`
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                  `}
                />
                <span
                  css={css`
                    color: ${tokens.color.text_danger};
                  `}
                >
                  {errorObj?.regex?.message}
                </span>
              </InputWithErrorContainer>

              <div
                css={css`
                  margin-left: ${tokens.spacer.x0};
                `}
              >
                <IconButton
                  icon="trash"
                  kind="secondary"
                  isDisabled={!hasIpBlockingManagePermission}
                  tooltipText={
                    (hasIpBlockingManagePermission
                      ? t('common:site-settings.ip-blocking.unblock-ip')
                      : t('common:site-settings.no-edit-access')) as string
                  }
                  onClick={() => remove(blockedIpIndex)}
                />
              </div>
            </div>
          );
        })}

        <ButtonsContainer>
          <Tooltip
            enabled={!hasIpBlockingManagePermission}
            content={t('common:site-settings.no-edit-access') as string}
            placement="right"
          >
            <div
              css={css`
                display: inline-block;
              `}
            >
              <Button isDisabled={!hasIpBlockingManagePermission} onClick={addIp}>
                {t('common:site-settings.ip-blocking.block-a-new-ip')}
              </Button>
            </div>
          </Tooltip>

          {!isCurrentIpAdded && hasIpBlockingManagePermission && (
            <Button
              css={css`
                margin-left: ${tokens.spacer.x2};
              `}
              onClick={selfBlockIp}
              kind="secondary"
            >
              {t('common:site-settings.ip-blocking.block-your-current-ip')}
            </Button>
          )}

          <div
            css={css`
              margin: ${tokens.spacer.x3} 0;
              color: ${tokens.color.text_light};
            `}
          >
            {t('common:site-settings.ip-blocking.all-sites-note', {
              orgName: site.organization.name,
            })}
          </div>
          <Separator />
          <Tooltip
            enabled={!hasIpBlockingManagePermission}
            content={t('common:site-settings.no-edit-access') as string}
            placement="right"
          >
            <div
              css={css`
                display: inline-block;
              `}
            >
              <Button
                type="submit"
                isDisabled={
                  !hasIpBlockingManagePermission || isSubmitting || !isDirty || !canSubmit
                }
                isLoading={!canSubmit}
                loadingText={t('common:site-settings.saving-changes')}
              >
                {t('common:site-settings.save-changes')}
              </Button>
            </div>
          </Tooltip>
        </ButtonsContainer>
      </Section>
    </form>
  );
};

// FIXME: import/no-default-export
// eslint-disable-next-line import/no-default-export
export default useIpBlocking;
